<template>
  <form
    :class="classes"
    :qa-id="qaId"
    :data-testid="dataTestid"
    @submit.prevent
    @keypress.enter="onKeyPressEnter"
    @keydown.esc="onKeyPressEscap"
  >
    <article
      :qa-id="`${qaId}_content`"
      :data-testid="`${dataTestid}_content`"
    >
      <slot>{{ $t('formControls.layout.emptyForm') }}</slot>
    </article>

    <footer
      :qa-id="`${qaId}_footer`"
      :data-testid="`${dataTestid}_footer`"
    >
      <div
        class="zod-form--footer--left"
        :qa-id="`${qaId}_footer_left`"
        :data-testid="`${dataTestid}_footer_left`"
      >
        <slot name="formFooterLeft" />
      </div>

      <div
        class="zod-form--footer--right"
        :qa-id="`${qaId}_footer_right`"
        :data-testid="`${dataTestid}_footer_right`"
      >
        <slot name="formFooterMessage" />
        <slot name="formFooterRight">
          <template v-if="formHasErrors">
            <span
              v-if="formHasErrors"
              class="text-error cursor-pointer mr-3"
              v-tooltip="{ content: displayFormErrors, theme: 'error'}"
              :qa-id="`${qaId}_error_tooltip`"
              :data-testid="`${dataTestid}_error_tooltip`"
              @mouseover="showErrorList"
            > <span
                :data-testid="`${dataTestid}_errors_icon`"
                class="far fa-exclamation-circle fa-lg mr-1"
              />
              <strong :data-testid="`${dataTestid}_issues_found`">
                {{ $t('formControls.validation.issuesFound') }}
              </strong>
            </span>
          </template>

          <ZoFormActionButton
            v-for="(button, index) in actionButtons"
            :key="index"
            :button="button"
            @clicked="actionButtonClicked"
          />
        </slot>
      </div>
    </footer>
  </form>
</template>

<script>
import { defineComponent, nextTick } from 'vue';

import ZoFormActionButton from '@/components/form-controls/FormActionButton.vue';

export default defineComponent({
  components: {
    ZoFormActionButton,
  },

  props: {
    qaId: {
      type: String,
      default: 'form',
    },
    dataTestid: {
      type: String,
      default: 'form',
    },
    validations: {
      type: Object,
      default: () => ({}),
    },
    actionButtons: {
      type: Array,
      default: () => [],
    },
    useAltLayout: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      jumpToErrors: false,
    };
  },

  computed: {
    classes() {
      return {
        'zod-form': true,
        'has-errors': this.formHasErrors,
        'use-alt-layout': this.useAltLayout,
      };
    },
    formErrorCount() {
      return this.validations.$errors?.length;
    },
    formHasErrors() {
      return this.formErrorCount > 0;
    },
    displayFormErrors() {
      const errorList = [];
      let errors = '';

      if (this.formHasErrors) {
        this.validations.$errors.forEach((error) => {
          const text = error.value
            ? this.$t(`formControls.validation.keys.${error.value}`) || error.value
            : error.$params.label;
          if (errorList.findIndex((e) => e === `<li>${text}</li>`) === -1) {
            errorList.push(`<li>${text}</li>`);
          }
        });

        errors = `<ul class="my-1 pl-3">${errorList.sort().join('')}</ul>`;
      }

      return `<div class="p-1">${this.$t('formControls.validation.general')} ${errors}</div>`;
    },
  },

  methods: {
    onKeyPressEnter(event) {
      if (event.target.tagName === 'TEXTAREA') {
        return;
      }
      if (event.target.classList && event.target.classList.contains('js--cancel')) {
        this.onKeyPressEscap(event);
      }
      else {
        const submitButton = this.actionButtons?.find((button) => button.type === 'submit');
        this.actionButtonClicked(event, submitButton);
      }
    },
    onKeyPressEscap(event) {
      const closeButton = this.actionButtons.find((button) => button.type !== 'submit');
      this.actionButtonClicked(event, closeButton);
    },
    async actionButtonClicked(event, button) {
      event.preventDefault();

      if (button?.type === 'submit') {
        this.jumpToErrors = true;
        const submitButton = this.$el.querySelector(`.${button.class}`);

        // @todo Figure out why this eslint is failing.
        // eslint-disable-next-line vue/valid-next-tick
        await this.bounceFocus();
        if (typeof this.validations?.$validate === 'function') {
          this.validations?.$validate();
        }
        if (!this.formHasErrors) {
          if (submitButton) {
            submitButton.focus();
          }

          await button.action.call(null, event);
        }
        else if (button.error) {
          event.stopPropagation();
        }
      }
      else {
        await button?.action.call(null, event);
      }
    },

    async bounceFocus() {
      const formErrorId = this.validations?.$errors?.length > 0 ? this.validations.$errors[0]?.$params?.id : '';
      if (formErrorId && this.jumpToErrors) {
        this.$el.querySelector(`#${formErrorId}`)?.focus();
      }
    },
    async showErrorList() {
      this.jumpToErrors = true;
      this.validations.$validate();
      this.bounceFocus();
    },
  },
});
</script>

<style lang="scss">
.zod-form {
  --column-min-size: 22em;
  --column-max-size: 1fr;
  --column-layout: repeat(
    auto-fit,
    [col-start] minmax(var(--column-min-size), var(--column-max-size)) [col-end]
  );
  --local-section-footer-padding: 0;

  &.use-alt-layout {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    article {
      flex: 1;
      text-wrap: balance;
    }

    footer {
      flex: none;
    }
  }

  article {
    position: relative;
    display: grid;
    grid-template-columns: var(--column-layout);
    grid-gap: var(--space-xsmall);
    align-items: start;
    overflow: var(--local-overflow, unset);
  }

  fieldset {
    border: none;
    background: var(--gray-lighten-10);
    border-radius: var(--radius-default);
    padding: var(--space-xsmall);
    margin: 0;
    height: 100%;
  }

  legend {
    display: contents;
    font-family: var(--font-heading), sans-serif;
    font-size: var(--text-size-bump);
    font-weight: bold;
  }

  footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    bottom: 0;
    z-index: 1;
    border-radius: 0 0 var(--radius-default) var(--radius-default);
    padding: var(--local-footer-padding, 0);
  }

  &--column {
    display: grid;
    grid-template-columns: var(--column-layout);
    grid-gap: var(--space-xsmall);
  }

  &--row {
    --row-margin-top: var(--space-small);
    --row-grid-gap: var(--space-small);
    --row-column-fallback: repeat(auto-fit);

    &:not(.is-block) {
      display: grid;
      grid-gap: var(--row-grid-gap);
      grid-template-columns: var(--cols, var(--row-column-fallback));
      grid-auto-flow: column;
      align-items: var(--row-align-items, baseline);
    }

    &.is-block label {
      display: block;
    }

    &--text {
      padding: var(--space-min) 0;
    }

    &.is-tight {
      &--vertical {
        --row-margin-top: var(--space-min);
      }
      &--horizontal {
        --row-grid-gap: var(--space-min);
      }
    }

    + * {
      margin-top: var(--row-margin-top);
    }
  }

  &--control {
    position: relative;
  }

  &--footer {
    &--left:not(:empty) {
      padding: var(--local-footer-left-padding, var(--space-small));
    }
    &--right:not(:empty) {
      padding: var(--local-footer-right-padding, var(--space-small));
    }
    &--right{
      display: flex;
      justify-content: end;
      flex-direction: row;
      align-items: center;
    }
  }
}
</style>
